import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import queryString from 'query-string';

import queryStringOptions from '@/constants/queryStringOptions';

import { getToken } from './getToken';

export type Params = string | string[] | number | boolean | null | undefined;

type ResponseData<R> = R extends AxiosResponse<infer D> ? D : never;

const ajax = {
  async get({
    url,
    params,
    config
  }: {
    url: string;
    params?: { [key: string]: Params };
    config?: AxiosRequestConfig;
  }): Promise<AxiosResponse> {
    return axios({
      headers: this.getHeaders(url),
      method: 'get',
      params,
      paramsSerializer: {
        serialize: (params) => queryString.stringify(params, queryStringOptions)
      },
      url,
      ...config
    });
  },

  async post<T = never, R = AxiosResponse<T>>({
    url,
    data,
    config,
    headers
  }: {
    url: string;
    data: T;
    config?: AxiosRequestConfig;
    headers?: { [key: string]: string };
  }): Promise<R> {
    const defaultHeaders =
      data instanceof FormData ? this.getHeaders(url, null) : this.getHeaders(url);
    return axios<ResponseData<R>, AxiosResponse<ResponseData<R>>, T>({
      data,
      headers: headers || defaultHeaders,
      method: 'post',
      url,
      ...config
    }) as Promise<R>;
  },

  async put<T = never, R = AxiosResponse<T>>({
    url,
    data,
    config
  }: {
    url: string;
    data: T;
    config?: AxiosRequestConfig;
  }): Promise<R> {
    return axios<ResponseData<R>, AxiosResponse<ResponseData<R>>, T>({
      data,
      headers: this.getHeaders(url),
      method: 'put',
      url,
      ...config
    }) as Promise<R>;
  },
  async patch<T = never, R = AxiosResponse<T>>({
    url,
    data,
    config
  }: {
    url: string;
    data?: T;
    config?: AxiosRequestConfig;
  }): Promise<R> {
    return axios<ResponseData<R>, AxiosResponse<ResponseData<R>>, T>({
      data,
      headers: this.getHeaders(url),
      method: 'patch',
      url,
      ...config
    }) as Promise<R>;
  },
  async delete<T = never, R = AxiosResponse<T>>({
    url,
    data,
    config
  }: {
    url: string;
    data?: T;
    config?: AxiosRequestConfig;
  }): Promise<R> {
    return axios<ResponseData<R>, AxiosResponse<ResponseData<R>>, T>({
      data,
      headers: this.getHeaders(url),
      method: 'delete',
      url,
      ...config
    }) as Promise<R>;
  },
  getHeaders(url: string, contentType: string | null = 'application/json') {
    const urlObject = new URL(url);
    const host = urlObject.origin;

    const token = getToken(host);

    const headers: { [key: string]: string } = {};

    if (contentType !== null) {
      headers['Content-Type'] = contentType;
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
};

export default ajax;
